import { tagged } from "@mirage/service-logging";
import { Observable, Subject, type Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";

import { mergeMessages } from "@mirage/service-conversation/service/conversationsSync/mergeMessages";
import {
  allowOpQueueCancellations,
  OpQueueCancelationError,
  SerialOpQueue,
} from "@mirage/service-conversation/service/conversationsSync/serialOpQueue";
import type {
  ConversationMessagesServerMetadata,
  ConversationsDataSyncInterface,
  ConversationsMetadataSyncInterfaceInternal,
  DataAPI,
  DataStorage,
  StoredConversation,
} from "@mirage/service-conversation/service/conversationsSync/types";
import type {
  Conversation,
  ConversationClientContext,
  ConversationMessage,
} from "@mirage/service-conversation/types";
import throttle from "lodash/throttle";

const logger = tagged("conversationsSync/dataSync");

// sync logic for all conversations data (e.g. messages and drafts)
export function createConversationsDataSync(
  storage: DataStorage,
  api: DataAPI,
  metadataSync: ConversationsMetadataSyncInterfaceInternal,
): ConversationsDataSyncInterface {
  const conversationDataSyncs = new Map<
    string,
    ReturnType<typeof createConversationDataSync>
  >();

  // get a conversation data sync for a given conversation ID or create a new one if it doesn't exist
  const getConversationDataSync = (
    conversationID: string,
    knownServerConversationID: string | undefined,
  ): ReturnType<typeof createConversationDataSync> => {
    let conversationDataSync = conversationDataSyncs.get(conversationID);
    if (!conversationDataSync) {
      conversationDataSync = createConversationDataSync(
        storage,
        api,
        metadataSync,
        conversationID,
        knownServerConversationID,
      );
      conversationDataSyncs.set(conversationID, conversationDataSync);
    }
    return conversationDataSync;
  };

  const getConversationDataSyncByServerId = async (
    serverConversationID: string,
  ) => {
    // check if there is a running conversation with the given server conversation ID
    const allDataSyncConversations = await Promise.all(
      Array.from(conversationDataSyncs.entries()).map(
        async ([conversationID, conversationDataSync]) => {
          const conversation = await conversationDataSync.get();
          return {
            id: conversationID,
            conversation,
            conversationDataSync,
          };
        },
      ),
    );
    const foundConversation = allDataSyncConversations.find(
      ({ conversation }) =>
        conversation.serverConversationId === serverConversationID,
    );
    if (foundConversation) {
      logger.info(
        `found in-memory conversation with server conversation ID: ${serverConversationID}`,
      );
      return {
        dataSync: foundConversation.conversationDataSync,
        id: foundConversation.id,
      };
    }

    // if not, check if there is a conversation in storage with the given server conversation ID
    const storedConversations = await storage.loadConversations();
    for (const [conversationId, conversation] of Object.entries(
      storedConversations,
    )) {
      if (conversation.serverConversationId === serverConversationID) {
        logger.info(
          `found stored conversation with server conversation ID: ${serverConversationID}`,
        );
        return {
          dataSync: getConversationDataSync(
            conversationId,
            serverConversationID,
          ),
          id: conversationId,
        };
      }
    }

    // create a new conversation data sync for the given server conversation ID if we have never
    // seen it before
    const newConversationID = uuidv4();
    logger.info(
      `creating new conversation for server conversation ID: ${serverConversationID}`,
    );
    return {
      dataSync: getConversationDataSync(
        newConversationID,
        serverConversationID,
      ),
      id: newConversationID,
    };
  };

  const deleteConversations: ConversationsDataSyncInterface["deleteConversations"] =
    async (conversationIDs) => {
      for (const conversationID of conversationIDs) {
        const conversationDataSync = conversationDataSyncs.get(conversationID);
        if (conversationDataSync) {
          conversationDataSyncs.delete(conversationID);
        }
        // TODO: deal with in-flight update calls (worst case it may come back after deletion)
      }

      storage.deleteConversations(conversationIDs);
      metadataSync.deleteCachedConversationMetadatas(conversationIDs);

      // call APIs to delete conversations
      await api.deleteConversations(conversationIDs);
    };

  return {
    getConversation: (conversationID) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.get();
    },
    getConversationByServerId: async (serverConversationID) => {
      const { dataSync, id } =
        await getConversationDataSyncByServerId(serverConversationID);
      const conversation = await dataSync.get();
      return {
        ...conversation,
        id,
      };
    },
    refreshConversation: (conversationID) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      conversationDataSync.refresh();
    },
    createNewConversation: async () => {
      const newConversationID = uuidv4();
      const conversationDataSync = getConversationDataSync(
        newConversationID,
        undefined,
      );
      const conversation = await conversationDataSync.get();
      return {
        ...conversation,
        id: newConversationID,
      };
    },
    updateConversationMessages: (
      conversationID,
      messages,
      response,
      clientContext,
      requestIdOverride,
      stackId,
    ) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        response?.serverConversationId,
      );
      return conversationDataSync.updateMessages(
        messages,
        response,
        clientContext,
        requestIdOverride,
        stackId,
      );
    },
    updateConversationDraft: (conversationID, draft) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.updateDraft(draft);
    },
    getConversationObservable: (conversationID) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.getObservable();
    },
    deleteConversations,
  };
}

/**
 * Types of operations that can be performed serially + asynchronously against API + storage.
 */
type ConversationOpType = "refresh" | "updateDraft" | "updateMessages";

// sync logic for a single conversation
function createConversationDataSync(
  storage: DataStorage,
  api: DataAPI,
  metadataSync: ConversationsMetadataSyncInterfaceInternal,
  conversationID: string,
  knownServerConversationID: string | undefined,
) {
  const loadFromAPI = async (
    serverConversationID: string,
    prev?: Conversation | undefined,
  ): Promise<Conversation> => {
    const apiConversation = await api.getConversation(serverConversationID);

    let conversation = apiConversation.conversation;
    if (prev && prev.messages.length === conversation.messages.length) {
      // augment API conversation properties with previous conversation. these are properties that
      // are not returned by the API but are returned by message responses.
      conversation = {
        ...conversation,
        suggestedQueries: prev.suggestedQueries,
        progressUpdates: prev.progressUpdates,
      };
    }

    // save the conversation to storage + update metadata
    throttledSaveConversation(conversation);
    // update (or insert) cached metadata with the latest metadata from the server
    metadataSync.updateCachedConversationMetadata(
      serverConversationID,
      apiConversation.metadata,
    );

    return conversation;
  };

  const firstLoad = async (): Promise<StoredConversation> => {
    const storedConversations = await storage.loadConversations();
    const storedConversation: StoredConversation | undefined =
      storedConversations[conversationID];

    const serverConversationID =
      knownServerConversationID || storedConversation?.serverConversationId;
    if (serverConversationID) {
      // if local conversation is dirty, start by saving it to the server
      if (storedConversation?.isDirty) {
        if (storedConversation.markdownDraft) {
          await api.updateConversationDraft(
            serverConversationID,
            storedConversation.markdownDraft,
          );
        } else {
          logger.error(
            `found dirty conversation but no draft to save: ${conversationID}`,
          );
        }
      }

      // load the conversation from the server
      return await loadFromAPI(serverConversationID, storedConversation);
    } else {
      // no known server conversation ID, no-op as this is a new conversation
    }

    return (
      storedConversation || {
        messages: [],
        suggestedQueries: [],
        markdownDraft: undefined,
        serverConversationId: undefined,
      }
    );
  };

  /* latest cached conversation data */
  let cachedConversation: Promise<Conversation> = firstLoad();

  /* subject that emits latest conversation */
  const conversationSubject = new Subject<Conversation>();

  /* serial queue for managing operations against the API and storage */
  const opQueue = new SerialOpQueue<ConversationOpType>();

  const get = (): Promise<StoredConversation> => {
    if (!cachedConversation) {
      cachedConversation = firstLoad();
    }
    return cachedConversation;
  };

  /* update and emit the latest cached conversation */
  const updateCachedConversation = (conversation: StoredConversation): void => {
    cachedConversation = Promise.resolve(conversation);
    conversationSubject.next(conversation);
  };

  /* clears all pending ops with the given type */
  const clearOpsWithType = (type: ConversationOpType) => {
    const pendingOps = opQueue.getOps().filter((op) => op.opInfo === type);
    for (const op of pendingOps) {
      opQueue.cancelOp(op.id);
    }
  };

  /* if a refresh is pending, cancel it and re-queue a refresh op */
  const requeueRefreshIfPending = (): void => {
    const hasPendingRefresh = opQueue
      .getOps()
      .find((op) => op.opInfo === "refresh");
    if (hasPendingRefresh) {
      opQueue.cancelOp(hasPendingRefresh.id);
      queueRefresh();
    }
  };

  const queueRefresh = async () => {
    // cancel all previously queued refresh ops
    clearOpsWithType("refresh");

    const existingConversation = await get();
    const serverConversationID = existingConversation.serverConversationId;
    if (!serverConversationID) {
      // no server conversation ID, no-op as this is a new conversation
      return;
    }

    try {
      const updatedConversation = await opQueue.queueOp<Conversation>(() => {
        return loadFromAPI(serverConversationID, existingConversation);
      }, "refresh").promise;

      // emit and save as latest
      updateCachedConversation(updatedConversation);
    } catch (error) {
      if (error instanceof OpQueueCancelationError) {
        // ignore cancelled ops, this just means we have queued another refresh op superceding this
        return;
      }
      throw error;
    }
  };

  const updateMessages = async (
    messages: ConversationMessage[],
    response?: ConversationMessagesServerMetadata,
    clientContext?: ConversationClientContext,
    requestIdOverride?: string,
    stackId?: string,
  ): Promise<Conversation> => {
    const existingConversation = await get();
    const updatedConversation: Conversation = {
      ...existingConversation,
      messages: mergeMessages(existingConversation.messages, messages),
    };
    if (response) {
      // newly received response from server, update aux metadata from response
      updatedConversation.serverConversationId = response.serverConversationId;
      updatedConversation.suggestedQueries = response.suggestedQueries;
      updatedConversation.markdownDraft = response.markdownDraft;
      updatedConversation.progressUpdates = response.progressUpdates;
      updatedConversation.assistRequestId = response.assistRequestId;
      updatedConversation.spanId = response.spanId;
    }

    if (clientContext) {
      // update client context if present
      updatedConversation.clientContext = clientContext;
    }

    if (requestIdOverride) {
      // update request ID with override if present
      updatedConversation.assistRequestId = requestIdOverride;
    }

    if (stackId) {
      updatedConversation.stackId = stackId;
    }

    // cancel all previously queued updateMessages ops as they are already outdated
    clearOpsWithType("updateMessages");

    // queue an update to storage without waiting for them to complete
    opQueue
      .queueOp(async () => {
        await throttledSaveConversation(updatedConversation);
      }, "updateMessages")
      .promise.catch(allowOpQueueCancellations);

    // interrupt any ongoing refresh and place it at the back of the queue to avoid races where
    // user made an update that gets temporarily wiped out by the refresh
    requeueRefreshIfPending();

    // update cached conversation + emit
    updateCachedConversation(updatedConversation);

    return updatedConversation;
  };

  const updateDraft = async (draft: string): Promise<Conversation> => {
    const existingConversation = await get();
    const updatedConversation: StoredConversation = {
      ...existingConversation,
      markdownDraft: draft,
      isDirty: true, // mark as dirty to persist to server on next open if page closed before saving
    };

    // cancel all previously queued updateDraft ops as they are already outdated
    clearOpsWithType("updateDraft");

    // queue an update to storage + API without waiting for them to complete
    opQueue
      .queueOp(async () => {
        // save draft to storage
        await throttledSaveConversation(updatedConversation);

        const serverConversationID = updatedConversation.serverConversationId;
        if (serverConversationID) {
          // update draft on server
          await api.updateConversationDraft(serverConversationID, draft);
          // mark draft as saved after API call completes
          const savedConversation = {
            ...existingConversation,
            isDirty: false,
          };
          await throttledSaveConversation(savedConversation);
        } else {
          // no server conversation ID, no-op as this is a new conversation
        }
      }, "updateDraft")
      .promise.then(async () => {
        const latestConversation = await get();
        if (latestConversation.markdownDraft !== draft) {
          // draft has changed since the API op was queued, no need to update
          return;
        }
        // update cached conversation's isDirty flag since it was saved to server
        updateCachedConversation({
          ...latestConversation,
          isDirty: false,
        });
        return;
      })
      .catch(allowOpQueueCancellations);

    // interrupt any ongoing refresh and place it at the back of the queue to avoid races where
    // user made an update that gets temporarily wiped out by the refresh
    requeueRefreshIfPending();

    // update cached conversation + emit
    updateCachedConversation(updatedConversation);

    return updatedConversation;
  };

  const getObservable = (): Observable<Conversation> => {
    return new Observable((subscriber) => {
      let subscription: Subscription | undefined;

      // When subscribing, start by sending them the initial state
      get()
        .then((conversation) => {
          subscriber.next(conversation);
          subscription = conversationSubject.subscribe(subscriber);
          return subscription;
        })
        .catch((error) => {
          return subscriber.error(error);
        });
      return () => {
        subscription?.unsubscribe();
      };
    });
  };

  const throttledSaveConversation = throttle(
    (conversation: StoredConversation) => {
      return storage.saveConversation(conversationID, conversation);
    },
    1000, // throttle writes to storage to avoid overloading the storage layer
    {
      trailing: true,
    },
  );

  return {
    get,
    refresh: queueRefresh,
    updateMessages,
    updateDraft,
    getObservable,
  };
}
